import React, { FC, useEffect, useMemo, useState } from "react";
import { languageType } from "../../TerminalModes/Normal/NormalMode/terminalModeShared";
import { ExtraInputFields } from "../../../../utils/Admin/CategoriesAndServicesTable/mapExtraInputFields";
import {
  filterExtraInputChars,
  validateExtraInput,
} from "../../../../utils/Terminal/validateExtraInput";
import classes from "../TerminalMisScanModal/TerminalMisScanModal.module.scss";

type TerminalExtraDataModalProps = {
  language: languageType;
  description?: string | null;
  settings: ExtraInputFields;
  onCancel: () => void;
  onSubmit: (value: string) => void;
};

const copy = {
  cancel: { ru: "Отмена", by: "Адмена", en: "Cancel" },
  submit: { ru: "Подтвердить", by: "Пацвердзіць", en: "Confirm" },
  placeholder: { ru: "Введите данные", by: "Увядзіце даныя", en: "Enter data" },
};

export const TerminalExtraDataModal: FC<TerminalExtraDataModalProps> = ({
  language,
  description,
  settings,
  onCancel,
  onSubmit,
}) => {
  const [value, setValue] = useState("");
  const showKeypad = Boolean(settings.extraInputDigitsOnly);
  const maxLength = Math.min(settings.extraInputMaxLength ?? 128, 128);

  useEffect(() => {
    setValue("");
  }, [description, settings.extraInputDigitsOnly]);

  const validation = useMemo(
    () => validateExtraInput(value, settings),
    [value, settings],
  );

  const applyValue = (next: string) => {
    setValue(filterExtraInputChars(next, settings));
  };

  const handleSubmit = () => {
    const result = validateExtraInput(value, settings);
    if (!result.ok) return;
    onSubmit(value.trim());
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true">
      <div className={classes.card}>
        <div className={classes.title}>{description || copy.placeholder[language]}</div>
        {validation.message && value.length > 0 && (
          <div className={classes.error}>{validation.message}</div>
        )}
        {!showKeypad && (
          <input
            className={classes.cardInput}
            type="text"
            autoComplete="off"
            autoFocus
            maxLength={maxLength}
            placeholder={copy.placeholder[language]}
            value={value}
            onChange={(e) => applyValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        )}
        {showKeypad && (
          <>
            <div className={classes.code} aria-hidden="true">
              {value}
            </div>
            <div className={classes.keypad}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"].map(
                (key) => (
                  <button
                    key={key}
                    type="button"
                    className={classes.key}
                    onClick={() => {
                      if (key === "⌫") {
                        setValue((prev) => prev.slice(0, -1));
                        return;
                      }
                      if (key === "OK") {
                        handleSubmit();
                        return;
                      }
                      applyValue(`${value}${key}`);
                    }}
                  >
                    {key}
                  </button>
                ),
              )}
            </div>
          </>
        )}
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.secondary}
            onClick={onCancel}
          >
            {copy.cancel[language]}
          </button>
          <button
            type="button"
            className={classes.primary}
            disabled={!validation.ok}
            onClick={handleSubmit}
          >
            {copy.submit[language]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TerminalExtraDataModal;
